import { type ChangeEvent, useMemo, useReducer, useState } from 'react';

import { mustGetAccountPlan } from '@/data/account/selectors/accountPlan';
import { getLdClient } from '@/ld-redux';
import useNotice from '@/notices/hooks/useNotice';
import Platform from '@/shared/api/platform';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import { useBoolToggle } from '@/shared/hooks/useToggle';
import Button from '@/shared/ui/Button';
import FontIcon from '@/shared/ui/FontIcon';
import { Tooltip } from '@/shared/ui/Tooltip';
import LD from '@/shared/vendor/launchDarkly';
import { type WiwState, useWiwSelector } from '@/store';

import { Code, Collapse, Paper, Switch, TextInput } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';

import '@/shared/dev/styles/LaunchDarklyPanel.scss';

type Flags = {
  pinned: Record<string, boolean>;
  unpinned: Record<string, boolean>;
};

export function LaunchDarklyPanel() {
  const { errorNotice } = useNotice();
  const currentUser = useWiwSelector(getAuthUser);
  const currentAccount = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(mustGetAccountPlan);
  const flagsRaw = useWiwSelector((state: WiwState) => state.LD);
  const [pinnedFlags, setPinnedFlags] = useLocalStorage<string[]>({
    key: 'ld-flags-pinned',
    defaultValue: [],
  });

  const [flagFilter, setFlagFilter] = useState('');
  const [showContext, toggleContext] = useBoolToggle(false);

  // manages the disabled state for flags so we don't have people toggling flags
  // on and off in quick succession
  const [flagChanging, toggleFlagChanging] = useReducer(
    (state: Record<string, boolean>, action: string) => {
      return { ...state, [action]: !state[action] };
    },
    Object.keys(flagsRaw).reduce<Record<string, boolean>>((acc, flag) => {
      if (flag === 'isLDReady') {
        return acc;
      }

      const ldClient = getLdClient();
      if (!ldClient) {
        acc[flag] = false;
        return acc;
      }

      const variation = ldClient.variationDetail(flag, false);
      if (variation.reason?.kind === 'OFF') {
        // you can't change the flag if it's off
        acc[flag] = true;
      } else {
        acc[flag] = false;
      }

      return acc;
    }, {}),
  );

  // separates the flags into pinned and unpinned for display
  const flags = useMemo(() => {
    return Object.entries(flagsRaw).reduce<Flags>(
      (acc, [flag, value]) => {
        if (pinnedFlags.includes(flag)) {
          acc.pinned[flag] = value;
        } else {
          acc.unpinned[flag] = value;
        }

        return acc;
      },
      { pinned: {}, unpinned: {} },
    );
  }, [flagsRaw, pinnedFlags]);

  const contextJson = useMemo(() => {
    const context = LD.buildMultiContext(currentUser, currentAccount, accountPlan);
    return JSON.stringify(context, null, 2);
  }, [currentUser, currentAccount, accountPlan]);

  const pinFlag = (flag: string) => {
    setPinnedFlags([...pinnedFlags, flag]);
  };

  const unpinFlag = (flag: string) => {
    setPinnedFlags(pinnedFlags.filter(f => f !== flag));
  };

  const toggleFlag = (flagName: string) => {
    return async (e: ChangeEvent<HTMLInputElement>) => {
      try {
        toggleFlagChanging(flagName);
        await Platform.patch({ targetValue: e.currentTarget.checked }, `/dev-tools/ld/${flagName}`);
      } catch (err: unknown) {
        console.error(err);
        errorNotice('There was an error updating the flag.');
      } finally {
        toggleFlagChanging(flagName);
      }
    };
  };

  const openFlag = (flag: string) => {
    // `default` is the wheniwork project
    window.open(
      `https://app.launchdarkly.com/projects/default/flags/${flag}/targeting?env=production&env=dev&env=staging&env=acceptance&selected-env=dev`,
      '_blank',
    );
  };

  const getReason = (flag: string) => {
    const ldClient = getLdClient();
    if (!ldClient) {
      return;
    }

    const variation = ldClient.variationDetail(flag, false);
    if (!variation.reason) {
      return;
    }

    let reasonText = '';
    if (variation.reason.kind === 'ERROR') {
      reasonText = 'There was an error evaluating the flag. Try again later.';
      if (variation.reason.errorKind) {
        reasonText += ` More details: ${variation.reason.errorKind}.`;
      }
    } else if (variation.reason.kind === 'OFF') {
      reasonText = 'The flag is off and returning the configured OFF value.';
    } else if (variation.reason.kind === 'PREREQUISITE_FAILED') {
      reasonText = 'The flag is considered off because a prerequisite was off or failed to evaluate.';
      if (variation.reason.prerequisiteKey) {
        reasonText += ` The prerequisite flag is "${variation.reason.prerequisiteKey}".`;
      }
    } else if (variation.reason.kind === 'FALLTHROUGH') {
      reasonText = 'This is the default value for the flag.';
    } else if (variation.reason.kind === 'RULE_MATCH') {
      if (typeof variation.reason.ruleIndex !== 'number' || typeof variation.reason.ruleId !== 'string') {
        reasonText = 'The user matched a rule.';
      } else {
        reasonText = `Matched the ${numberToOrdinal(variation.reason.ruleIndex + 1)} rule.`;
      }
    } else if (variation.reason.kind === 'TARGET_MATCH') {
      reasonText = 'The user was specifically targeted to receive the flag value.';
    }

    return reasonText;
  };

  return (
    <div className="ld-panel">
      <div className="ld-panel__context">
        <h4 onClick={toggleContext}>
          LD Context Details
          {showContext ? <FontIcon icon="chevron-up" /> : <FontIcon icon="chevron-down" />}
        </h4>

        <Collapse in={showContext}>
          <Code block>{contextJson}</Code>
        </Collapse>
      </div>

      {Object.keys(flags.pinned).length > 0 && (
        <Paper className="ld-panel__pinned" shadow="xs" radius="md" p="md" withBorder>
          <h4>
            <FontIcon icon="pushpin" />
            Pinned Flags
            <Button className="ml-auto" color="secondary" size="sm" onClick={() => setPinnedFlags([])}>
              Clear All
            </Button>
          </h4>

          {Object.entries(flags.pinned).map(([flag, value]) => (
            <div key={flag} className="ld-panel__flag">
              <Switch label={flag} checked={value} onChange={toggleFlag(flag)} disabled={flagChanging[flag]} />
              <Tooltip label={getReason(flag)}>
                <FontIcon icon="help" onClick={() => openFlag(flag)} />
              </Tooltip>
              <FontIcon icon="close" onClick={() => unpinFlag(flag)} />
            </div>
          ))}
        </Paper>
      )}

      <TextInput
        className="ld-panel__filter"
        placeholder="Search for a flag"
        value={flagFilter}
        onChange={e => setFlagFilter(e.target.value)}
        rightSection={flagFilter && <FontIcon icon="close-circled" onClick={() => setFlagFilter('')} />}
      />

      {Object.entries(flags.unpinned)
        // only working with boolean flags for the time being
        .filter(
          ([flag, value]) =>
            flag !== 'isLDReady' && flag.toLowerCase().includes(flagFilter.toLowerCase()) && typeof value === 'boolean',
        )
        .sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .map(([flag, value]) => (
          <div key={flag} className="ld-panel__flag">
            <Switch label={flag} checked={value} onChange={toggleFlag(flag)} disabled={flagChanging[flag]} />
            <Tooltip label={getReason(flag)}>
              <FontIcon icon="help" onClick={() => openFlag(flag)} />
            </Tooltip>
            <FontIcon icon="pushpin" onClick={() => pinFlag(flag)} />
          </div>
        ))}
    </div>
  );
}

function numberToOrdinal(n: number) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}
